import type { OrgMetadata } from '~/common/auth/metadata/metadata';
import { hasAccessGrantingStatus } from '~/common/stripe/plan-utils';

/**
 * Client-side check for whether to show the payment modal.
 *
 * This is a simplified client-side check that looks at org metadata directly.
 * It checks:
 * 1. If user has any active subscription in the new Stripe system
 * 2. If user has legacy entitlement (and hasn't been migrated to new system)
 * 3. If user is a super admin (bypasses payment requirement)
 *
 * Note: For authoritative access checks, use the server-side getAccessStatus.
 */
export function shouldShowPaymentModal({
  orgMetadata,
  isSuperAdmin,
}: {
  orgMetadata: OrgMetadata | undefined;
  isSuperAdmin: boolean;
}): boolean {
  // Super admins always have access
  if (isSuperAdmin) {
    return false;
  }

  if (!orgMetadata) {
    return true;
  }

  // Check new Stripe-based system - active, trialing, or past_due (non-hobby) grants access
  const hasActiveSubscription = orgMetadata.stripe?.subscriptions.some((sub) => hasAccessGrantingStatus(sub)) ?? false;
  if (hasActiveSubscription) {
    return false;
  }

  // Check legacy entitlement (only if org has never been on new system)
  const hasBeenOnNewSystem = orgMetadata.stripe != null;
  if (orgMetadata.entitlement && !hasBeenOnNewSystem) {
    return false;
  }

  return true;
}
